const getBracketDiscount = (value, brackets) => {
    if (!brackets || brackets.length === 0) return 0;
    // Find matching bracket
    const bracket = brackets.find(b => value > b.from && value <= b.to);
    if (bracket) return bracket.discount_pct;
    // If above last bracket, use last bracket's discount
    const lastBracket = brackets[brackets.length - 1];
    if (value > lastBracket.to) return lastBracket.discount_pct;
    return 0;
};

export const calculateGrainDiscounts = (params) => {
    const {
        grossWeight,
        tareWeight,
        moistureMeasured,
        impurityMeasured,
        technicalShrinkPct = 0,
        policy = {
            standardMoisture: 14.0,
            impurityTolerance: 0.0,
            factors: []
        },
        defectMeasurements = {}
    } = params;

    const results = {
        steps: [],
        grossWeight: Math.abs(grossWeight - tareWeight),
        p1_afterImpurity: 0,
        p2_afterMoisture: 0,
        p3_afterShrink: 0,
        finalWeight: 0,
        totalDiscountWeight: 0,
        discounts: {},
        qualityDiscounts: {}
    };

    let currentWeight = results.grossWeight;

    // 1. Impurity / Matéria Estranha (MEI)
    const impurityFactor = (policy.factors || []).find(f => f.code === 'IMPUREZA');
    let impDiscount = 0;

    if (impurityFactor && impurityFactor.brackets && impurityFactor.brackets.length > 0) {
        const impDiscPct = getBracketDiscount(impurityMeasured, impurityFactor.brackets);
        impDiscount = results.grossWeight * (impDiscPct / 100);
    } else {
        const impExc = Math.max(0, impurityMeasured - policy.impurityTolerance);
        impDiscount = results.grossWeight * (impExc / 100);
    }

    results.discounts['IMPUREZA'] = impDiscount;
    currentWeight -= impDiscount;
    results.p1_afterImpurity = currentWeight;
    results.steps.push({
        name: 'Impureza',
        measured: impurityMeasured,
        tolerance: policy.impurityTolerance,
        discount: impDiscount,
        after: currentWeight
    });

    // 2. Moisture (Umidade) 
    const moistureFactor = (policy.factors || []).find(f => f.code === 'UMIDADE');
    let moistDiscount = 0;

    if (moistureFactor && moistureFactor.brackets && moistureFactor.brackets.length > 0) {
        const moistDiscPct = getBracketDiscount(moistureMeasured, moistureFactor.brackets);
        moistDiscount = currentWeight * (moistDiscPct / 100);
        currentWeight -= moistDiscount;
    } else if (moistureMeasured > policy.standardMoisture) {
        // Fallback to Constant Dry Mass formula
        const p2 = currentWeight * (100 - moistureMeasured) / (100 - policy.standardMoisture);
        moistDiscount = currentWeight - p2;
        currentWeight = p2;
    }

    results.discounts['UMIDADE'] = moistDiscount;
    results.p2_afterMoisture = currentWeight;
    results.steps.push({
        name: 'Umidade',
        measured: moistureMeasured,
        standard: policy.standardMoisture,
        discount: moistDiscount,
        after: currentWeight
    });

    // 3. Technical Shrink (Quebra Técnica)
    const shrinkDiscount = currentWeight * (technicalShrinkPct / 100);
    results.discounts['QUEBRA_TECNICA'] = shrinkDiscount;
    currentWeight -= shrinkDiscount;
    results.p3_afterShrink = currentWeight;
    results.steps.push({
        name: 'Quebra Técnica',
        pct: technicalShrinkPct,
        discount: shrinkDiscount,
        after: currentWeight
    });

    // 4. Quality Defects (Ardidos, Avariados, etc.)
    (policy.factors || []).forEach(factor => {
        // Skip already processed factors
        if (factor.code === 'IMPUREZA' || factor.code === 'UMIDADE') return;

        const measuredRaw = defectMeasurements[factor.code] || 0;
        const measured = Math.max(0, measuredRaw - (factor.tolerance || 0));

        let discountPct = 0;
        if (measured > 0) {
            discountPct = getBracketDiscount(measuredRaw, factor.brackets);
        }

        if (factor.discount_mode === 'PESO') {
            const d = currentWeight * (discountPct / 100);
            results.qualityDiscounts[factor.code] = {
                measured: measuredRaw,
                discountPct,
                discount: d,
                mode: 'PESO'
            };
            currentWeight -= d;
        } else {
            results.qualityDiscounts[factor.code] = {
                measured: measuredRaw,
                discountPct,
                mode: 'PRECO'
            };
        }
    });

    results.finalWeight = currentWeight;
    results.totalDiscountWeight = results.grossWeight - results.finalWeight;

    return results;
};
